import { Controller } from '@hotwired/stimulus'
import {
  AllCommunityModule,
  ModuleRegistry,
  createGrid,
  InfiniteRowModelModule,
  TextFilterModule,
  themeAlpine
} from 'ag-grid-community'
import {
  CellSelectionModule,
  ClipboardModule,
  SetFilterModule,
  LicenseManager,
  ColumnMenuModule,
  RowNumbersModule,
  ContextMenuModule
} from 'ag-grid-enterprise'
import { format, parseISO, getDaysInMonth, getDay, setDate } from 'date-fns'
import AccessibleHeaderText from './grids/components/AccessibleHeaderText'
import GridConfigurationHelper from './grids/grid_configuration_helper'
import GridQueueHelper from './grids/queue_helper.js'
import GridFetchHelper from './grids/fetch_helper'
import { turboRequest } from '../../helpers/turbo_helper'
import { openModal, setFrameSrc, disable, enable } from '../../helpers/custom_events_helper'
import { GRID_EVENTS, RESOLUTIONS } from './grids/grid_events'

ModuleRegistry.registerModules([
  AllCommunityModule,
  InfiniteRowModelModule,
  CellSelectionModule,
  ClipboardModule,
  TextFilterModule,
  SetFilterModule,
  ColumnMenuModule,
  RowNumbersModule,
  ContextMenuModule
])

const MAX_DAYS = 31
const DEFAULT_COLUMN_WIDTH = 128
const BACKGROUND_COLORS = {
  nonEditable: '#F1F1F1',
  temp: '#FFF9E3',
  default: '#FFFFFF',
  transparent: 'transparent',
  suspended: '#DFCFF0'
}

const textFilterParams = {
  filter: 'agTextColumnFilter',
  filterParams: {
    filterOptions: ['contains'],
    maxNumConditions: 1
  },
  sortable: true
}

const caseInsensitiveComparator = (valueA, valueB) => {
  const a = (valueA || '').toString().toLowerCase()
  const b = (valueB || '').toString().toLowerCase()
  return a.localeCompare(b)
}

const formatNumber = value => {
  return parseFloat(value.toFixed(2))
}

const myTheme = themeAlpine.withParams({
  browserColorScheme: 'light',
  cellHorizontalPaddingScale: 0.5,
  headerFontSize: 13,
  headerVerticalPaddingScale: 0.5,
  iconSize: 14,
  rowVerticalPaddingScale: 0.5
})

// constants for columns to maintain references, avoid typos
const COLUMNS = {
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  CAREGIVER: 'Caregiver',
  CARE_RECEIVER: 'Care receiver',
  CLIENT: 'Client',
  EMPLOYEE: 'Employee',
  SERVICE_DEF: 'Service Definition Name',
  FUNDING_SOURCE: 'Funding Source Name'
}

const ROW_HEADERS = [
  COLUMNS.FIRST_NAME,
  COLUMNS.LAST_NAME,
  COLUMNS.CAREGIVER,
  COLUMNS.CARE_RECEIVER,
  COLUMNS.CLIENT,
  COLUMNS.EMPLOYEE,
  COLUMNS.SERVICE_DEF,
  COLUMNS.FUNDING_SOURCE
]

export default class extends Controller {
  static targets = [
    'grid',
    'month',
    'toggleWeekendColumns',
    'saveChanges',
    'finishSubmit',
    'loadingCount',
    'uploadMonth',
    'templateLink'
  ]

  static values = {
    valuesUrl: String,
    gridId: String,
    uploadId: String,
    licenseKey: String,
    usesScreenReader: Boolean,
    forCaregiver: Boolean,
    forEmployee: Boolean,
    // Grid.row_cell_key_fields: the row fields that, with the date, make TempServiceRecord#cell_key.
    cellKeyFields: Array,
    saveConfigurationUrl: String,
    tableConfigData: Object,
    clientPathTemplate: String,
    cellHistoryUrl: String,
    // This is used to determine how many customers are in a batch when we load the data
    customersFetchedPerBatch: {
      type: Number,
      default: 40
    },
    // This is used to determine how many items are in a batch when we process the queue creating temp service records
    bulkCreateBatchSize: {
      type: Number,
      default: 100
    },
    maxConcurrentCreateRequests: {
      type: Number,
      default: 5
    },
    maxConcurrentFetchRequests: {
      type: Number,
      default: 4
    }
  }

  connect() {
    LicenseManager.setLicenseKey(this.licenseKeyValue)

    this.liveRegion = document.getElementById('live-region-polite')

    this.uploadId = this.uploadIdValue
    this.setUploadId = this.setUploadId.bind(this)
    this.handleGlobalKeyDown = this.handleGlobalKeyDown.bind(this)
    this.pendingModalOperations = 0
    this.handleModalOperationStarted = this.handleModalOperationStarted.bind(this)
    this.handleModalOperationCompleted = this.handleModalOperationCompleted.bind(this)
    // Seed the config helper with the persisted "Hide Weekends" state so any
    // subsequent save (column move, resize, etc.) preserves the toggle.
    this.configHelper = new GridConfigurationHelper(
      null, // gridApi will be set later
      this.saveConfigurationUrlValue,
      this.liveRegion,
      this.usesScreenReaderValue,
      this.hasToggleWeekendColumnsTarget && this.toggleWeekendColumnsTarget.checked
    )
    this.queueHelper = new GridQueueHelper({
      bulkCreateBatchSize: this.bulkCreateBatchSizeValue,
      uploadId: this.uploadId,
      updateSaveButtonState: this.updateSaveButtonState?.bind(this),
      maxConcurrentCreateRequests: this.maxConcurrentCreateRequestsValue,
      valuesUrl: this.valuesUrlValue
    })
    this.gridFetchHelper = new GridFetchHelper({
      valuesUrl: `${this.valuesUrlValue}/${this.gridIdValue}`,
      customersFetchedPerBatch: this.customersFetchedPerBatchValue,
      uploadId: this.uploadId,
      maxConcurrentFetchRequests: this.maxConcurrentFetchRequestsValue
    })

    this.setupGrid()

    window.addEventListener(GRID_EVENTS.setUploadId, this.setUploadId)
    window.addEventListener(GRID_EVENTS.parsed, this.handleParsed)
    window.addEventListener(GRID_EVENTS.updateCellValue, this.handleCellValueUpdate)
    window.addEventListener(GRID_EVENTS.modalOperationStarted, this.handleModalOperationStarted)
    window.addEventListener(GRID_EVENTS.modalOperationCompleted, this.handleModalOperationCompleted)
    document.addEventListener('keydown', this.handleGlobalKeyDown)
    document.addEventListener(GRID_EVENTS.revealCell, this.handleRevealCell)
    document.addEventListener(GRID_EVENTS.cellResolved, this.handleCellResolved)
    document.addEventListener(GRID_EVENTS.decideCell, this.handleDecideCell)
    document.addEventListener(GRID_EVENTS.decisionConfirmed, this.handleDecisionConfirmed)
  }

  disconnect() {
    window.removeEventListener(GRID_EVENTS.setUploadId, this.setUploadId)
    window.removeEventListener(GRID_EVENTS.parsed, this.handleParsed)
    window.removeEventListener(GRID_EVENTS.updateCellValue, this.handleCellValueUpdate)
    window.removeEventListener(GRID_EVENTS.modalOperationStarted, this.handleModalOperationStarted)
    window.removeEventListener(GRID_EVENTS.modalOperationCompleted, this.handleModalOperationCompleted)
    document.removeEventListener('keydown', this.handleGlobalKeyDown)
    document.removeEventListener(GRID_EVENTS.revealCell, this.handleRevealCell)
    document.removeEventListener(GRID_EVENTS.cellResolved, this.handleCellResolved)
    document.removeEventListener(GRID_EVENTS.decideCell, this.handleDecideCell)
    document.removeEventListener(GRID_EVENTS.decisionConfirmed, this.handleDecisionConfirmed)

    if (this.configHelper) {
      this.configHelper.cleanup()
    }

    this.gridTarget.innerHTML = ''
  }

  setupGrid() {
    const gridOptions = this.buildGridOptions()
    this.gridApi = createGrid(this.gridTarget, gridOptions)
    this.loadAllData(this.monthTarget.value)
  }

  async loadAllData(monthAsString) {
    try {
      this.loadedMonth = null
      this.gridApi.setGridOption('loading', true)
      this.gridApi.setGridOption('rowData', [])

      const uploadId = this.gridFetchHelper.uploadId
      const result = await this.gridFetchHelper.fetchAllData(monthAsString)

      this.gridApi.setGridOption('rowData', result.rows)
      this.gridApi.setGridOption('pinnedBottomRowData', this.calculatePinnedBottomRow())
      this.gridApi.setGridOption('loading', false)
      this.loadedMonth = monthAsString
      // The upload whose cells the rows hold, so a different upload's file reloads them all.
      this.loadedUploadId = uploadId ? String(uploadId) : null
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading data:', error)
        document.dispatchEvent(
          new CustomEvent('toast', {
            detail: {
              failure: true,
              message: 'Failed to load grid data. Please try refreshing the page.'
            }
          })
        )
        this.gridApi.setGridOption('loading', false)
      }
    }
  }

  async refreshUploadRows(monthAsString) {
    try {
      this.gridApi.setGridOption('loading', true)
      const { rows } = await this.gridFetchHelper.fetchAllData(monthAsString, { uploadMembersOnly: true })
      const fresh = new Map(rows.map(row => [this.rowKeyFor(row), row]))
      const updated = []
      this.gridApi.forEachNode(node => {
        const row = node.data && fresh.get(this.rowKeyFor(node.data))
        if (!row) return
        node.setData(row)
        updated.push(node)
      })
      if (updated.length) this.gridApi.refreshCells({ rowNodes: updated, force: true })
      this.gridApi.setGridOption('pinnedBottomRowData', this.calculatePinnedBottomRow())
      this.gridApi.setGridOption('loading', false)
      this.loadedUploadId = String(this.gridFetchHelper.uploadId)
    } catch (error) {
      if (error.name === 'AbortError') return
      console.error('Error refreshing upload rows:', error)
      await this.loadAllData(monthAsString)
    }
  }

  onFilterChanged() {
    this.gridApi.setGridOption('pinnedBottomRowData', this.calculatePinnedBottomRow())
  }

  buildGridOptions() {
    return {
      onCellEditingStarted: this.handleCellEditingStarted.bind(this),
      onCellValueChanged: this.handleCellValueChanged.bind(this),
      onCellEditingStopped: this.handleCellEditingStopped.bind(this),
      onCellClicked: this.handleCellClicked.bind(this),
      onCellKeyDown: this.handleCellKeyDown.bind(this),
      onFilterChanged: this.onFilterChanged.bind(this),
      onColumnMoved: this.configHelper.handleColumnConfigurationChanged.bind(this.configHelper),
      onColumnVisible: this.configHelper.handleColumnConfigurationChanged.bind(this.configHelper),
      onColumnPinned: this.configHelper.handleColumnConfigurationChanged.bind(this.configHelper),
      onColumnResized: this.configHelper.handleColumnConfigurationChanged.bind(this.configHelper),
      onSortChanged: this.configHelper.handleColumnConfigurationChanged.bind(this.configHelper),
      defaultColDef: this.defaultColumnDefinition(),
      columnDefs: this.buildColumnDefinitions(),
      enterNavigatesVerticallyAfterEdit: true,
      suppressRowHoverHighlight: true,
      stopEditingWhenCellsLoseFocus: true,
      undoRedoCellEditing: true,
      undoRedoCellEditingLimit: 100,
      cellSelection: { handle: { mode: 'range' }, enableHeaderHighlight: true },
      rowNumbers: !this.usesScreenReaderValue,
      theme: myTheme,
      suppressContextMenu: false,
      getRowClass: params => {
        if (params.data && params.data.isSpacerRow) {
          return 'spacer-row'
        }
        return ''
      },
      allowContextMenuWithControlKey: false,
      // AG-Grid's own themed tooltip rather than the native title: it appears quickly and reliably,
      // where the browser's is slow to show and easy to miss.
      enableBrowserTooltips: false,
      tooltipShowDelay: 200,
      tooltipInteraction: true,
      getContextMenuItems: params => {
        const result = []
        result.push('cut')
        result.push('copy')

        if (!this.uploadId) {
          result.push('separator')
          result.push('export')
        }

        const cellHistoryItem = this.buildCellHistoryMenuItem(params)
        if (cellHistoryItem) {
          result.push('separator')
          result.push(cellHistoryItem)
        }

        return result
      },
      getRowStyle: params => {
        if (params.node.rowPinned === 'bottom') {
          return {
            fontWeight: '600',
            boxShadow: 'inset 0 4px 0 -2px #ccc'
          }
        }
        // Hide spacer rows for zendesk
        if (params.data && Object.keys(params.data).length === 0) {
          return {
            height: '0px',
            visibility: 'hidden'
          }
        }
        return null
      },
      onFirstDataRendered: () => {
        // ag-grid has no native capability to implement row headers, so we do some post-processing here
        // so that certain cell values will be read out during screen reader navigation
        const querySelectors = ROW_HEADERS.map(t => `div[role="gridcell"][col-id="${t}"]`)
        this.gridTarget.querySelectorAll(querySelectors.join(',')).forEach(node => {
          node.setAttribute('role', 'rowheader')
        })
      },
      onGridReady: params => {
        this.gridApi = params.api
        this.queueHelper.grid = params.api
        this.queueHelper.gridApi = params.api
        this.configHelper.setGridApi(this.gridApi)

        setTimeout(() => {
          this.configHelper.setIsInitialLoad(false)
        }, 2000)
      },
      onPasteStart: () => {
        this.pasteStart = new Date()
      }
    }
  }

  defaultColumnDefinition() {
    return {
      editable: true,
      sortable: false,
      width: 64
    }
  }

  buildColumnDefinitions() {
    const selectedDate = parseISO(this.monthTarget.value)

    let staticColumns = this.buildStaticColumns()
    const dynamicColumns = this.buildDynamicColumns(selectedDate)

    let allColumns = [...staticColumns, ...dynamicColumns]

    if (this.tableConfigDataValue && Object.keys(this.tableConfigDataValue).length > 0) {
      return this.configHelper.applyConfigurationToColumnDefs(allColumns, this.tableConfigDataValue)
    } else {
      return allColumns
    }
  }

  createClickableLink(params) {
    const { data, node, colDef, value } = params
    if (!data || node?.rowPinned === 'bottom') return value

    let key

    switch (colDef.field) {
      case COLUMNS.CAREGIVER:
        key = 'Caregiver Mon Ami ID'
        break
      case COLUMNS.CARE_RECEIVER:
        key = 'Care receiver Mon Ami ID'
        break
      case COLUMNS.CLIENT:
        key = 'Client Mon Ami ID'
        break
      case COLUMNS.FIRST_NAME:
      case COLUMNS.LAST_NAME:
        key = 'Client Mon Ami ID'
        break
      default:
        return value
    }

    const clientId = data[key]
    if (!clientId) return value

    const link = document.createElement('a')
    link.href = this.clientPathTemplateValue.replace('{clientId}', clientId)
    link.target = '_blank'
    link.innerText = value
    link.style.textDecoration = 'underline dotted #9E9E9E'
    link.style.color = 'unset'

    return link
  }

  nameColumnConfig(textFilterParams, sharedConfig, pinConfig, tooltip = false) {
    return {
      ...textFilterParams,
      ...sharedConfig,
      ...pinConfig,
      sortable: true,
      suppressColumnsToolPanel: true,
      lockVisible: true,
      cellRenderer: params => {
        return this.createClickableLink(params)
      },
      tooltipValueGetter: params => {
        if (params.node.rowPinned === 'bottom' && tooltip) {
          return params.value
        }
      }
    }
  }

  hiddenNameColumnConfig(textFilterParams, sharedConfig) {
    return {
      ...textFilterParams,
      ...sharedConfig,
      sortable: false,
      hide: true
    }
  }

  buildStaticColumns() {
    const sharedConfig = {
      width: DEFAULT_COLUMN_WIDTH,
      editable: false,
      lockPinned: this.usesScreenReaderValue,
      sortable: true,
      comparator: caseInsensitiveComparator
    }
    const pinConfig = {
      pinned: this.usesScreenReaderValue ? false : 'left'
    }

    const nameColumns = []

    if (this.forCaregiverValue) {
      nameColumns.push(
        {
          field: COLUMNS.CAREGIVER,
          ...this.nameColumnConfig(textFilterParams, sharedConfig, pinConfig, true)
        },
        {
          field: COLUMNS.CARE_RECEIVER,
          ...this.nameColumnConfig(textFilterParams, sharedConfig, pinConfig)
        }
      )
    } else if (this.forEmployeeValue) {
      nameColumns.push({
        field: COLUMNS.CLIENT,
        ...this.nameColumnConfig(textFilterParams, sharedConfig, pinConfig, true)
      })
    } else {
      nameColumns.push(
        {
          field: COLUMNS.FIRST_NAME,
          ...this.nameColumnConfig(textFilterParams, sharedConfig, pinConfig, true)
        },
        {
          field: COLUMNS.LAST_NAME,
          ...this.nameColumnConfig(textFilterParams, sharedConfig, pinConfig)
        }
      )
    }

    if (this.forEmployeeValue) {
      nameColumns.push({
        field: COLUMNS.EMPLOYEE,
        ...this.nameColumnConfig(textFilterParams, sharedConfig, pinConfig)
      })
    }

    return [
      ...nameColumns,
      ...(this.forCaregiverValue
        ? [
            {
              field: 'Caregiver Mon Ami ID',
              ...this.hiddenNameColumnConfig(textFilterParams, sharedConfig)
            },
            {
              field: 'Care receiver Mon Ami ID',
              ...this.hiddenNameColumnConfig(textFilterParams, sharedConfig)
            }
          ]
        : [
            {
              field: 'Client Mon Ami ID',
              ...this.hiddenNameColumnConfig(textFilterParams, sharedConfig)
            }
          ]),
      ...(this.forEmployeeValue
        ? [
            {
              field: 'Employee Mon Ami ID',
              ...this.hiddenNameColumnConfig(textFilterParams, sharedConfig)
            }
          ]
        : []),
      {
        field: COLUMNS.SERVICE_DEF,
        ...sharedConfig,
        ...pinConfig,
        filter: 'agSetColumnFilter'
      },
      {
        field: COLUMNS.FUNDING_SOURCE,
        ...sharedConfig,
        ...pinConfig,
        filter: 'agSetColumnFilter'
      },
      {
        field: 'Program Name',
        ...sharedConfig,
        filter: 'agSetColumnFilter'
      },
      {
        field: 'Date of Birth',
        ...sharedConfig,
        sortable: true,
        hide: true,
        suppressColumnsToolPanel: this.forCaregiverValue || this.forEmployeeValue, // Temporarily hiding them for caregivers and employees; proper handling will be implemented in a separate PR.
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue) => {
            const cellDate = new Date(cellValue)
            if (cellDate < filterLocalDateAtMidnight) {
              return -1
            } else if (cellDate > filterLocalDateAtMidnight) {
              return 1
            } else {
              return 0
            }
          },
          filterOptions: ['inRange', 'greaterThan', 'lessThan', 'equals', 'greaterThanOrEqual', 'lessThanOrEqual'],
          defaultOption: 'inRange',
          maxNumConditions: 1
        }
      },
      {
        field: 'County',
        hide: true,
        ...sharedConfig,
        ...textFilterParams,
        suppressColumnsToolPanel: this.forCaregiverValue || this.forEmployeeValue // Temporarily hiding them for caregivers and employees; proper handling will be implemented in a separate PR.
      },
      {
        field: 'Total Cost',
        resizable: true,
        width: 120,
        hide: true,
        suppressColumnsToolPanel: this.uploadId
      },
      {
        field: 'Month Total',
        headerName: 'Month Total',
        valueGetter: params => {
          if (!params.data) return 0
          return Object.keys(params.data)
            .filter(key => /^days_\d+$/.test(key))
            .reduce((sum, key) => {
              const val = parseFloat(params.data[key])
              return sum + (isNaN(val) ? 0 : val)
            }, 0)
        },
        valueFormatter: params => formatNumber(params.value),
        ...sharedConfig,
        ...pinConfig
      }
    ]
  }

  buildDynamicColumns(selectedDate) {
    this.columnsMonth = format(selectedDate, 'yyyy-MM')
    return Array.from({ length: MAX_DAYS }, (_, i) => {
      const headerName = this.headerNameFromDate(setDate(selectedDate, i + 1))
      return {
        field: `days_${i + 1}`,
        headerName,
        headerComponentParams: {
          innerHeaderComponent: AccessibleHeaderText,
          innerHeaderComponentParams: { dateIndex: i + 1 }
        },
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: params => ({
          min: 0,
          max: 1000,
          precision: params.data && params.data.accepts_partial_units ? 2 : 0
        }),
        valueParser: params => {
          const val = parseFloat(params.newValue)
          return isNaN(val) ? null : val
        },
        editable: params => {
          return (
            !!this.uploadId &&
            !params.data?.care_relationship_disabled &&
            params.data?.[`days_${i + 1}_is_editable`] &&
            !params.data?.[`days_${i + 1}_is_unauthorized`] &&
            !params.data?.[`days_${i + 1}_is_multiple`] &&
            !params.data?.[`days_${i + 1}_has_automation`] &&
            // A cell still needing a decision is resolved only in the modal — never inline.
            !this.isPendingDecision(params.data, i + 1)
          )
        },
        // Swallow Backspace/Delete on a pending-decision cell so a range-clear can't queue a stray
        // delete — the modal is the only way to settle it.
        suppressKeyboardEvent: params => {
          const key = params.event?.key
          return (key === 'Backspace' || key === 'Delete') && this.isPendingDecision(params.data, i + 1)
        },
        cellRenderer: this.cellRendererForDayColumn(i + 1),
        cellStyle: this.cellStyleForDayColumn(i + 1),
        tooltipValueGetter: params => this.tooltipForCell(params, i + 1),
        cellClassRules: {
          'suspended-cell': params => params.data?.[`days_${i + 1}_is_suspended`] && this.uploadId,
          'edited-suspended-cell': params =>
            params.data?.[`days_${i + 1}_is_suspended`] &&
            this.uploadId &&
            params.data[`days_${i + 1}_is_temp`] &&
            params.data[`days_${i + 1}`] != params.data[`days_${i + 1}_previous_value`],
          // The change-mark for an edited cell, from its value vs. what's saved (no backend needed).
          'grid-cell--created': params => this.markForCell(params, i + 1) === 'created',
          'grid-cell--increased': params => this.markForCell(params, i + 1) === 'increased',
          'grid-cell--decreased': params => this.markForCell(params, i + 1) === 'decreased',
          'grid-cell--deleted': params => this.markForCell(params, i + 1) === 'deleted',
          // A corner tick + border flagging the cell's state, on top of any value colour. Extend by
          // adding a `grid-cell--tick-<state>` here and a matching modifier in grids.scss.
          'grid-cell--tick': params => !!this.cellTickForDay(i + 1, params.data),
          'grid-cell--disabled': params => this.cellIsDisabled(i + 1, params.data),
          'grid-cell--tick-suspended': params => this.cellTickForDay(i + 1, params.data) === 'suspended',
          'grid-cell--tick-decision': params => this.cellTickForDay(i + 1, params.data) === 'decision',
          'grid-cell--tick-skipped': params => this.cellTickForDay(i + 1, params.data) === 'skipped',
          // The cell whose review card is currently selected. Driven by a data flag so it survives
          // AG-Grid re-rendering the row on scroll.
          'grid-cell--active': params => !!params.data?.[`days_${i + 1}_active`]
        },
        hide: !this.shouldCellBeVisitable(selectedDate, i + 1, this.toggleWeekendColumnsTarget.checked),
        suppressHeaderMenuButton: true, // Stops the column header from seeing the 3 dots menu
        suppressMovable: true,
        suppressColumnsToolPanel: true, // Stops the column from being removed by choose columns menu
        lockPosition: 'right',
        width: 65
      }
    })
  }

  headerNameFromDate(date) {
    return format(date, 'M/d/yyyy')
  }

  cellRendererForDayColumn(day) {
    return params => {
      const isMultiple = params.data?.[`days_${day}_is_multiple`]
      const hasAutomation = params.data?.[`days_${day}_has_automation`]

      // An unusable value still awaiting a decision shows a "?" — to fix or skip — rather than the
      // value it can't apply. The raw entry is kept for the review card.
      if (this.isPendingDecision(params.data, day)) {
        const mark = document.createElement('span')
        mark.setAttribute('aria-hidden', 'true')
        mark.textContent = '?'
        const label = document.createElement('span')
        label.className = 'visually-hidden'
        label.textContent = this.pendingDecisionLabel(params.data, day)

        const unknown = document.createElement('span')
        unknown.className = 'grid-cell__unknown'
        unknown.append(mark, label)
        return unknown
      }

      if (isMultiple) {
        return `${params.value} *`
      } else if (!isMultiple && hasAutomation) {
        return `${params.value} †`
      } else {
        return params.value
      }
    }
  }

  // A day cell's change mark (created / increased / decreased / deleted, or null), from its value vs.
  // what's saved.
  markForCell(params, day) {
    if (params.node?.rowPinned) return null
    // A cell still awaiting a decision reads as a "?", not a value change.
    if (this.isPendingDecision(params.data, day)) return null
    return this.changeMarkForDay(day, params.data)
  }

  // The change-mark for a cell's data alone (no AG-Grid params), so the mark and the state tick
  // share one definition of "this cell was edited to a different value".
  changeMarkForDay(day, data) {
    if (!this.uploadId || !data) return null
    if (!data[`days_${day}_is_temp`]) return null

    const currentRaw = data[`days_${day}`]
    const previousRaw = data[`days_${day}_previous_value`]
    const current = currentRaw === null || currentRaw === undefined || currentRaw === '' ? 0 : Number(currentRaw)
    const previous = previousRaw === null || previousRaw === undefined || previousRaw === '' ? 0 : Number(previousRaw)

    if (current === previous) return null
    if (current === 0) return previous > 0 ? 'deleted' : null
    if (previous === 0) return 'created'
    return current > previous ? 'increased' : 'decreased'
  }

  // A day cell's corner tick (decision / skipped / suspended, or null), drawn by the matching
  // `grid-cell--tick-<state>` class. Disabled cells are a grey fill instead (see cellIsDisabled).
  cellTickForDay(day, data) {
    if (!data) return null
    // Before an upload is started the grid is read-only — everything is plain grey, no ticks. The
    // state marks only come into effect once editing begins.
    if (!this.uploadId) return null
    // An unusable value still awaiting a decision (fix or skip) — yellow, above the rest.
    if (this.isPendingDecision(data, day)) return 'decision'
    // A skipped cell applies nothing — a grey tick so it reads as deliberately set aside.
    if (data[`days_${day}_resolution`] === RESOLUTIONS.skipped) return 'skipped'
    if (this.isPendingWarning(data, day)) return 'suspended'
    if (data[`days_${day}_is_suspended`] && this.changeMarkForDay(day, data)) return 'suspended'
    return null
  }

  // An uploaded value still waiting in the decision modal: a temp value with no resolution. (Values typed
  // into the grid always carry one.)
  isPendingDecision(data, day) {
    return (
      !!data?.[`days_${day}_is_temp`] &&
      !data?.[`days_${day}_resolution`] &&
      data?.[`days_${day}_import_status`] !== 'warning'
    )
  }

  // An uploaded value on a suspended day, waiting to be accepted or skipped in the review panel. It
  // shows and edits like any value; editing it doesn't accept it.
  isPendingWarning(data, day) {
    return (
      !!data?.[`days_${day}_is_temp`] &&
      !data?.[`days_${day}_resolution`] &&
      data?.[`days_${day}_import_status`] === 'warning'
    )
  }

  // A day that can't be edited (inactive relationship, no authorization, or a locked day): shown as a
  // plain grey fill, with no tick or border — those are reserved for review states.
  cellIsDisabled(day, data) {
    if (!data || !this.uploadId) return false

    return Boolean(
      data.care_relationship_disabled ||
      data[`days_${day}_is_unauthorized`] ||
      data[`days_${day}_is_editable`] === false
    )
  }

  // A review card was selected: scroll the grid to its cell and mark that cell active (blue outline).
  handleRevealCell = event => {
    const { cellKey: wanted, day } = event.detail
    const match = this.nodeForCell(wanted, day)
    if (!match) return

    this.setActiveCell(wanted, day, match)

    const column = `days_${day}`
    this.showColumnIfHidden(column)
    // On narrow screens the drawer overlays the grid, so 'auto' could leave the cell under it.
    this.gridApi.ensureColumnVisible(column, this.reviewDrawerOverlapsGrid() ? 'start' : 'auto')
    this.gridApi.ensureNodeVisible(match, 'middle')
  }

  reviewDrawerOverlapsGrid() {
    const drawer = document.querySelector('.grid-review-drawer')
    if (!drawer) return false

    const drawerRect = drawer.getBoundingClientRect()
    const gridRect = this.element.getBoundingClientRect()
    return drawerRect.width > 1 && drawerRect.left < gridRect.right && drawerRect.right > gridRect.left
  }

  // Only one cell is active at a time — clear the previous, flag the new, and repaint both.
  setActiveCell(cellKey, day, node) {
    this.clearActiveCell()
    node.data[`days_${day}_active`] = true
    this.gridApi.refreshCells({ rowNodes: [node], columns: [`days_${day}`], force: true })
    this.activeCell = { cellKey, day }
  }

  clearActiveCell() {
    if (!this.activeCell) return
    const prev = this.nodeForCell(this.activeCell.cellKey, this.activeCell.day)
    const prevDay = this.activeCell.day
    this.activeCell = null
    if (!prev) return
    prev.data[`days_${prevDay}_active`] = false
    this.gridApi.refreshCells({ rowNodes: [prev], columns: [`days_${prevDay}`], force: true })
  }

  // A cell was settled from the rail (e.g. skipped): restamp its resolution so the mark updates
  // without reloading the whole grid.
  handleCellResolved = event => {
    const { cellKey: wanted, day, resolution, importStatus, unitCount } = event.detail
    const match = this.nodeForCell(wanted, day)
    if (!match) return

    // A settled cell is no longer the one being worked on, so drop the active highlight.
    if (this.activeCell && this.activeCell.cellKey === wanted && this.activeCell.day === day) {
      this.activeCell = null
    }
    match.data[`days_${day}_resolution`] = resolution
    match.data[`days_${day}_active`] = false

    // A skipped cell applies nothing, so it reverts to its committed baseline: the committed value,
    // no change mark, and multiple-ness based on the committed records alone (a skipped temp is not
    // one of the cell's records).
    if (resolution === RESOLUTIONS.skipped) {
      const committedValue = match.data[`days_${day}_committed_value`] ?? null
      match.data[`days_${day}`] = committedValue
      match.data[`days_${day}_previous_value`] = committedValue
      match.data[`days_${day}_is_multiple`] = (match.data[`days_${day}_committed_count`] || 0) > 1
    }

    // Unskipping a warning brings back the uploaded value it holds.
    if (resolution !== RESOLUTIONS.skipped && importStatus === 'warning') {
      match.data[`days_${day}`] = Number(unitCount)
    }

    this.gridApi.refreshCells({ rowNodes: [match], columns: [`days_${day}`], force: true })
  }

  // "Decide" on a multi-record / activity cell: scroll to it and open the decision modal, where the
  // uploaded value is placed on a record, added alongside, or skipped.
  handleDecideCell = event => {
    const { cellKey, day } = event.detail
    const match = this.nodeForCell(cellKey, day)
    if (!match) return

    const column = `days_${day}`
    this.showColumnIfHidden(column)
    this.gridApi.ensureColumnVisible(column)
    this.gridApi.ensureNodeVisible(match, 'middle')
    this.pendingDecision = { cellKey, day }
    this.showDecisionModal(match.data, String(day), match.id, column)
  }

  // The decision modal was confirmed: it closes itself, and we restamp just the settled cell with its
  // new value and mark — no full reload — plus refresh the rail counts and the column totals.
  handleDecisionConfirmed = event => {
    const target = this.pendingDecision
    this.pendingDecision = null
    document.dispatchEvent(new CustomEvent(GRID_EVENTS.cellsClassified))
    if (!target) return

    const match = this.nodeForCell(target.cellKey, target.day)
    if (!match) return

    const { choice, dayTotal, resolution } = event.detail || {}
    const day = target.day
    match.data[`days_${day}`] = dayTotal
    match.data[`days_${day}_resolution`] = resolution
    match.data[`days_${day}_active`] = false

    // The change mark compares against the committed baseline (what was on the day before the upload),
    // so a decision that lands on the same total reads as "no change", not an increase.
    const committedValue = match.data[`days_${day}_committed_value`]
    if (committedValue !== undefined) match.data[`days_${day}_previous_value`] = committedValue

    // Adding a record grows the cell's record count; deleting one shrinks it; an update leaves it.
    const committedCount = match.data[`days_${day}_committed_count`] || 0
    const count = choice === 'add_new' ? committedCount + 1 : choice === 'delete' ? committedCount - 1 : committedCount
    match.data[`days_${day}_is_multiple`] = count > 1

    if (this.activeCell && this.activeCell.cellKey === target.cellKey && this.activeCell.day === day) {
      this.activeCell = null
    }
    this.gridApi.refreshCells({ rowNodes: [match], columns: [`days_${day}`], force: true })
    this.gridApi.setGridOption('pinnedBottomRowData', this.calculatePinnedBottomRow())
  }

  showDecisionModal(data, day, rowNodeId, columnField) {
    const serviceDeliveredOn = format(parseISO(this.monthTarget.value), 'yyyy-MM-') + String(day).padStart(2, '0')

    const params = this.buildMultipleRecordsParams({
      data,
      serviceDeliveredOn,
      rowNodeId,
      columnField,
      uploadId: this.uploadId
    })

    const modalContainer = document.getElementById('grid-decision-modal')
    const url = `${this.valuesUrlValue}/${this.gridIdValue}/decision_modal?${params.toString()}`
    openModal(modalContainer)
    setFrameSrc(modalContainer, url)
  }

  // The grid row whose cell key for the given day matches, or null.
  nodeForCell(cellKey, day) {
    if (!this.gridApi || !cellKey) return null

    let match = null
    this.gridApi.forEachNode(node => {
      if (!match && node.data && this.cellKeyFor(node.data, day) === cellKey) match = node
    })
    return match
  }

  // The cell key for a grid row + day, matching TempServiceRecord#cell_key so a card can find its cell.
  cellKeyFor(data, day) {
    const serviceDeliveredOn = format(parseISO(this.monthTarget.value), 'yyyy-MM-') + String(day).padStart(2, '0')
    return `${this.rowKeyFor(data)}:${serviceDeliveredOn}`
  }

  rowKeyFor(data) {
    return this.cellKeyFieldsValue
      .map(field => data[field])
      .map(value => (value === null || value === undefined || value === '' ? '-' : value))
      .join(':')
  }

  // "Hide Weekends" hides the whole column; ensureColumnVisible only scrolls, so bring it back first.
  showColumnIfHidden(column) {
    if (!this.gridApi.getColumn) return
    const target = this.gridApi.getColumn(column)
    if (target && !target.isVisible()) this.gridApi.setColumnsVisible([column], true)
  }

  // The hover explanation for an edited day cell: what the edit does vs. what's saved.
  tooltipForCell(params, day) {
    if (params.node?.rowPinned) return ''
    const data = params.data
    if (!data) return ''
    if (data.care_relationship_disabled) return "This client's relationship is inactive, so this row can't be changed."
    if (data[`days_${day}_is_unauthorized`]) return "No service authorization for this day, so it can't be changed."
    if (data[`days_${day}_is_editable`] === false)
      return "This funding source isn't on the allow list for this date, so a service can't be recorded here."
    if (!this.uploadId) return ''
    if (this.isPendingDecision(data, day)) return this.pendingDecisionLabel(data, day)

    const who = this.cellSubject(data, day)
    const value = data[`days_${day}`]
    const previous = Number(data[`days_${day}_previous_value`])
    const mark = this.markForCell(params, day)

    if (this.isPendingWarning(data, day))
      return `${who}: ${value ?? 0} on a suspended day. Accept or skip it in the review panel.`
    if (mark === 'deleted') return `${who}: was ${previous}, removed.`
    if (mark === 'created') return `${who}: new value ${value} (cell was empty).`
    if (mark === 'increased' || mark === 'decreased') return `${who}: was ${previous} → ${value}.`
    return ''
  }

  // What a "?" cell tells a screen reader (and its tooltip): why it's waiting and how to settle it.
  pendingDecisionLabel(data, day) {
    const csvValue = data[`days_${day}_csv_value`]
    const found = csvValue ? ` (the file had '${csvValue}')` : ''
    return `${this.cellSubject(data, day)}: needs a decision${found}. Press Enter to decide.`
  }

  // "Aug 2, Jane Villager" — the day and client a tooltip is about.
  cellSubject(data, day) {
    const name = [data['First Name'], data['Last Name']].filter(Boolean).join(' ')
    const month = this.monthTarget?.value
    let dateLabel = `Day ${day}`
    if (month) {
      const parsed = parseISO(`${month.slice(0, 7)}-${String(day).padStart(2, '0')}`)
      if (!isNaN(parsed)) dateLabel = format(parsed, 'MMM d')
    }
    return name ? `${dateLabel}, ${name}` : dateLabel
  }

  cellStyleForDayColumn(day) {
    return params => {
      if (params.node?.rowPinned === 'bottom') {
        return { backgroundColor: 'unset' }
      }
      if (!this.uploadId) {
        return {
          backgroundColor: BACKGROUND_COLORS.nonEditable,
          cursor:
            params.data[`days_${day}_has_automation`] || params.data[`days_${day}_is_multiple`] ? 'pointer' : 'default'
        }
      }
      if (params.data?.care_relationship_disabled) {
        return { backgroundColor: BACKGROUND_COLORS.nonEditable }
      }
      if (!params.data) {
        return {}
      }
      if (!params.data[`days_${day}_is_editable`]) {
        return { backgroundColor: BACKGROUND_COLORS.nonEditable }
      }
      if (params.data[`days_${day}_is_unauthorized`]) {
        return { backgroundColor: BACKGROUND_COLORS.nonEditable }
      }
      if (params.data[`days_${day}_is_suspended`]) {
        return { backgroundColor: BACKGROUND_COLORS.suspended }
      }
      if (params.data[`days_${day}_is_multiple`] || params.data[`days_${day}_has_automation`]) {
        if (params.data[`days_${day}_is_temp`]) {
          return { backgroundColor: BACKGROUND_COLORS.temp, cursor: 'pointer' }
        } else {
          return { cursor: 'pointer' }
        }
      }
      if (
        params.data[`days_${day}_is_temp`] &&
        (() => {
          const currentValue = params.data[`days_${day}`] // null or number
          const previousValue = params.data[`days_${day}_previous_value`] // null or string

          // If both are null, no change
          if (currentValue === null && previousValue === null) {
            return false
          }

          // Convert previous value to number for comparison if it exists
          const previousNumber = previousValue === null ? null : Number(previousValue)

          // Return true if values are different
          return currentValue !== previousNumber
        })()
      ) {
        // Bold cells where the user changed an existing positive value to a different
        // positive value, so an update is visually distinct from a newly created cell
        // (yellow only) or a deletion (yellow with the value cleared).
        const previousValue = params.data[`days_${day}_previous_value`]
        const currentValue = params.data[`days_${day}`]
        const isUpdate =
          previousValue !== null &&
          previousValue !== undefined &&
          Number(previousValue) > 0 &&
          currentValue !== null &&
          currentValue !== undefined &&
          Number(currentValue) > 0
        return isUpdate
          ? { backgroundColor: BACKGROUND_COLORS.temp, fontWeight: 'bold' }
          : { backgroundColor: BACKGROUND_COLORS.temp }
      }
      return params.value
        ? { backgroundColor: BACKGROUND_COLORS.default }
        : { backgroundColor: BACKGROUND_COLORS.transparent }
    }
  }

  calculateMonthTotal(row) {
    let total = 0
    for (let i = 1; i <= MAX_DAYS; i++) {
      const dayField = `days_${i}`
      const val = parseFloat(row[dayField]) || 0
      total += val
    }
    return total
  }

  handleCellValueChanged(event) {
    const {
      value,
      oldValue,
      data: rowData,
      colDef: { field }
    } = event

    // Skip if they don't change anything on an empty cell
    if (value === undefined && oldValue === null) {
      return
    }

    const day = field.split('_')[1]
    const selectedDate = parseISO(this.monthTarget.value)

    this.queueHelper.addItemToQueue({
      customer_id: rowData.customer_id,
      client_care_relationship_id: rowData.client_care_relationship_id,
      employee_id: rowData.employee_id,
      program_id: rowData.program_id,
      service_funding_source_id: rowData.funding_source_id,
      provider_id: rowData.provider_id,
      location_provider_id: rowData.location_provider_id,
      service_definition_id: rowData.service_definition_id,
      service_delivered_on: format(selectedDate, 'yyyy-MM-') + day,
      service_record_id: rowData[`days_${day}_service_record_id`],
      unit_count: value,
      old_unit_count: event.oldValue,
      node: event.node,
      field: field,
      day: day
    })
  }

  monthChanged(event) {
    const selectedDate = parseISO(event.target.value)
    this.hideMonthColumnsDependingOnDays(selectedDate, true, this.toggleWeekendColumnsTarget.checked)

    this.loadAllData(event.target.value)

    // Keep the bulk-upload modal's month in sync with the grid, so opening it after changing the month
    // defaults to the month on screen rather than the one rendered at page load.
    if (this.hasUploadMonthTarget) this.uploadMonthTarget.value = event.target.value
    // Likewise the "Download template" link, whose rows (and an auth grid's members) are for that month.
    if (this.hasTemplateLinkTarget) {
      const href = new URL(this.templateLinkTarget.href)
      href.searchParams.set('report_month', event.target.value)
      this.templateLinkTarget.href = href.toString()
    }

    // Append the selected month to the URL
    this.replaceUrlWithMonth('report_month', event.target.value)
  }

  replaceUrlWithMonth(key, value) {
    const url = new URL(window.location.href)
    url.searchParams.set(key, value)
    window.history.replaceState(history.state, '', url)
  }

  hideMonthColumnsDependingOnDays(dateFnsDate, monthUpdated, hideWeekendColumns) {
    const columnsToShow = []
    const columnsToHide = []

    for (let i = 1; i <= MAX_DAYS; i++) {
      const columnName = `days_${i}`

      if (this.shouldCellBeVisitable(dateFnsDate, i, hideWeekendColumns)) {
        columnsToShow.push(columnName)
      } else {
        columnsToHide.push(columnName)
      }

      if (monthUpdated) {
        this.gridApi.getColumnDef(columnName).headerName = this.headerNameFromDate(setDate(dateFnsDate, i))
      }
    }
    if (monthUpdated) this.columnsMonth = format(dateFnsDate, 'yyyy-MM')

    if (columnsToShow.length) {
      this.gridApi.setColumnsVisible(columnsToShow, true)
    }
    if (columnsToHide.length) {
      this.gridApi.setColumnsVisible(columnsToHide, false)
    }

    this.gridApi.refreshCells({ force: true })
    this.gridApi.refreshHeader()
  }

  shouldCellBeVisitable(monthDate, i, hideWeekendColumns) {
    const daysInMonth = getDaysInMonth(monthDate)
    const date = setDate(monthDate, i)
    const dayOfWeek = getDay(date)
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

    if (i <= daysInMonth && (!hideWeekendColumns || (hideWeekendColumns && !isWeekend))) {
      return true
    } else {
      return false
    }
  }

  // The response swaps the header frame into edit mode — nothing to hide or disable here.
  startSubmission() {
    const reportMonth = this.monthTarget.value
    const url = new URL(`${this.valuesUrlValue}/${this.gridIdValue}/uploads`, window.location.origin)
    if (reportMonth) {
      url.searchParams.set('report_month', reportMonth)
    }

    turboRequest(url.toString(), {
      method: 'POST'
    })

    this.hideTotalCostColumn()
  }

  hideTotalCostColumn() {
    var columnDefs = this.gridApi.getColumnDefs()
    const updatedColumnDefs = columnDefs.map(colDef => {
      if (colDef.field === 'Total Cost') {
        return { ...colDef, suppressColumnsToolPanel: true }
      }
      return colDef
    })
    this.gridApi.setGridOption('columnDefs', updatedColumnDefs)
    this.gridApi.setColumnsVisible(['Total Cost'], false)
  }

  // A header swapped in by a month change or an upload renders the checkbox from no saved settings;
  // keep it showing whether weekends are actually hidden. (On first load it comes from the server.)
  toggleWeekendColumnsTargetConnected(checkbox) {
    if (this.configHelper) checkbox.checked = this.configHelper.hideWeekendColumns
  }

  // TODO: WHy does this get called twice when I click the checkbox?
  toggleWeekendColumns(event) {
    if (event.target.checked === undefined) {
      return
    }

    this.configHelper.setHideWeekendColumns(event.target.checked)
    this.hideMonthColumnsDependingOnDays(parseISO(this.monthTarget.value), false, event.target.checked)
  }

  setUploadId(event) {
    this.uploadId = event.detail.uploadId
    this.queueHelper.uploadId = this.uploadId
    this.gridFetchHelper.uploadId = this.uploadId

    // A submission is for a single month: move the grid to the upload's month. Compare with the month the
    // columns were built for; the swapped-in header's month field already says the upload's month.
    const month = event.detail.month
    if (month && this.hasMonthTarget && format(parseISO(month), 'yyyy-MM') !== this.columnsMonth) {
      this.monthTarget.value = month
      this.monthChanged({ target: this.monthTarget })
    }

    const url = new URL(window.location.href)
    url.searchParams.set('submission_id', this.uploadId)
    window.history.replaceState(history.state, '', url)

    this.gridApi.refreshCells({ force: true })
  }

  // The file finished parsing (from the broadcast or the overlay's poll; only the first does anything):
  // reload the cells into review mode in place, clear the overlay and fill the review drawer.
  handleParsed = async event => {
    const { uploadId, errored } = event.detail || {}
    if (!uploadId) return

    if (errored) {
      const url = new URL(window.location.href)
      url.searchParams.set('submission_id', uploadId)
      // Reloading onto the error banner isn't leaving the upload, so don't ask "Leave site?".
      window.dispatchEvent(new CustomEvent('unsaved-changes:remove'))
      window.location.href = url.toString()
      return
    }

    if (this.reviewLoadedFor === String(uploadId)) return
    this.reviewLoadedFor = String(uploadId)

    this.uploadId = String(uploadId)
    this.queueHelper.uploadId = this.uploadId
    this.gridFetchHelper.uploadId = this.uploadId

    const url = new URL(window.location.href)
    url.searchParams.set('submission_id', this.uploadId)
    window.history.replaceState(history.state, '', url)

    // Only the upload's rows change, so refresh just those when the month is loaded without another
    // upload's cells; after a re-upload the earlier upload's marks are on rows this file may not touch.
    const otherUploadLoaded = this.loadedUploadId && this.loadedUploadId !== this.uploadId
    const rowsFresh = this.loadedMonth === this.monthTarget.value && !otherUploadLoaded
    if (rowsFresh) await this.refreshUploadRows(this.monthTarget.value)
    else await this.loadAllData(this.monthTarget.value)

    document.querySelector('.grid-upload-loading')?.remove()
    const railFrame = document.getElementById('grid_upload_review_rail')
    if (railFrame && typeof railFrame.reload === 'function') railFrame.reload()
  }

  handleCellValueUpdate = event => {
    const detail = event.detail

    const node = this.gridApi.getRowNode(detail.row_node_id)
    if (!node?.data) return

    const field = detail.column_field
    node.data[field] = detail.new_total
    node.data[`${field}_is_temp`] = true
    node.data[`${field}_is_multiple`] = true
    // A user edit is a settled change, never a pending decision — carry a resolution so isPendingDecision
    // stays false. Without it the cell would paint a "?" and surface in the review sidebar.
    node.data[`${field}_resolution`] = RESOLUTIONS.update
    this.gridApi.refreshCells({ rowNodes: [node], force: true })
    this.gridApi.setGridOption('pinnedBottomRowData', this.calculatePinnedBottomRow())
  }

  handleCellEditingStarted() {
    this.updateSaveButtonState()
  }

  handleCellEditingStopped() {
    this.updateSaveButtonState()
  }

  handleCellClicked(event) {
    const { colDef, data, node } = event
    if (!colDef || !data) return

    const field = colDef.field
    if (!field || !field.startsWith('days_')) return

    const day = field.split('_')[1]

    // A pending cell is always settled in the decision modal — there is no inline fix. Any other
    // multi-record / activity cell opens the committed-records viewer.
    if (this.isPendingDecision(data, day)) {
      this.openDecisionForCell(data, day, node, field)
    } else if (data[`days_${day}_is_multiple`] || data[`days_${day}_has_automation`]) {
      this.showMultipleRecordsModal(data, day, node.id, field)
    }
  }

  // Enter on a focused (non-editing) pending-decision cell opens the modal — mirrors the click path.
  // (Backspace/Delete are swallowed per-column by suppressKeyboardEvent.)
  handleCellKeyDown(event) {
    const keyEvent = event.event
    const field = event.column?.getColId()
    if (!keyEvent || !field || !field.startsWith('days_') || !event.data) return

    const day = field.split('_')[1]
    if (keyEvent.key === 'Enter' && this.isPendingDecision(event.data, day)) {
      keyEvent.preventDefault()
      this.openDecisionForCell(event.data, day, event.node, field)
    }
  }

  openDecisionForCell(data, day, node, field) {
    this.pendingDecision = { cellKey: this.cellKeyFor(data, day), day }
    this.showDecisionModal(data, day, node.id, field)
  }

  showMultipleRecordsModal(data, day, rowNodeId, columnField) {
    const serviceDeliveredOn = format(parseISO(this.monthTarget.value), 'yyyy-MM-') + String(day).padStart(2, '0')

    const params = this.buildMultipleRecordsParams({
      data,
      serviceDeliveredOn,
      rowNodeId,
      columnField,
      uploadId: this.uploadId
    })

    const modalContainer = document.getElementById('grid-multiple-records-modal')
    const url = `${this.valuesUrlValue}/${this.gridIdValue}/multiple_service_records_modal?${params.toString()}`
    openModal(modalContainer)
    setFrameSrc(modalContainer, url)
  }

  buildCellHistoryMenuItem(params) {
    if (!this.cellHistoryUrlValue) return null

    const { column, node } = params
    const field = column?.getColDef()?.field
    if (!field || !field.startsWith('days_') || !node?.data) return null

    const day = field.split('_')[1]
    const data = node.data
    const serviceDeliveredOn = format(parseISO(this.monthTarget.value), 'yyyy-MM-') + String(day).padStart(2, '0')

    const query = new URLSearchParams()
    query.append('service_delivered_on', serviceDeliveredOn)
    const fields = [
      'program_id',
      'provider_id',
      'service_definition_id',
      'customer_id',
      'client_care_relationship_id',
      'employee_id',
      'location_provider_id'
    ]
    fields.forEach(name => {
      if (data[name] != null) query.append(name, data[name])
    })
    if (data.funding_source_id != null) {
      query.append('service_funding_source_id', data.funding_source_id)
    }

    const url = `${this.cellHistoryUrlValue}?${query.toString()}`

    return {
      name: 'View cell history',
      action: () => window.open(url, '_blank', 'noopener'),
      icon: '<span class="ag-icon ag-icon-eye"></span>'
    }
  }

  buildMultipleRecordsParams({ data, serviceDeliveredOn, rowNodeId, columnField, uploadId }) {
    const params = new URLSearchParams()

    params.append('service_delivered_on', serviceDeliveredOn)
    params.append('row_node_id', rowNodeId)
    params.append('column_field', columnField)

    const dataFields = [
      'program_id',
      'provider_id',
      'service_definition_id',
      'customer_id',
      'client_care_relationship_id',
      'employee_id',
      'location_provider_id'
    ]

    dataFields.forEach(field => {
      if (data[field] != null) {
        params.append(field, data[field])
      }
    })

    if (data.funding_source_id != null) {
      params.append('service_funding_source_id', data.funding_source_id)
    }

    if (uploadId) {
      params.append('upload_id', uploadId)
    }

    if (data[`${columnField}_is_suspended`]) {
      params.append('is_suspended', 'true')
    }

    return params
  }

  handleModalOperationStarted() {
    this.pendingModalOperations++
    this.updateSaveButtonState()
  }

  handleModalOperationCompleted() {
    this.pendingModalOperations--
    this.updateSaveButtonState()
  }

  // Save changes (edit mode) and Finish & Submit (review rail) both wait for every typed value to save.
  updateSaveButtonState() {
    const buttons = [...this.saveChangesTargets, ...this.finishSubmitTargets]
    if (buttons.length === 0 || !this.gridApi || !this.queueHelper) return

    const busy =
      this.gridApi.getEditingCells().length > 0 || this.queueHelper.hasQueueItems() || this.pendingModalOperations > 0

    buttons.forEach(button => {
      if (busy) {
        disable(button)
      } else {
        enable(button)
      }
      this.dispatchBsTooltipEvent(button, busy)
    })
  }

  // The rail re-renders Finish & Submit on every reload, so a reload mid-save must disable it again.
  finishSubmitTargetConnected() {
    this.updateSaveButtonState()
  }

  dispatchBsTooltipEvent(button, enable) {
    button.parentNode.dispatchEvent(new CustomEvent('enabled.bs.tooltip', { detail: { enable } }))
  }

  handleGlobalKeyDown(event) {
    // Check if we're in edit mode by looking for editing cells
    const editingCells = this.gridApi.getEditingCells()
    if (editingCells.length === 0) return

    const key = event.key
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
      // Prevent default behavior
      event.preventDefault()
      event.stopPropagation()

      // Get current focused cell
      const focusedCell = this.gridApi.getFocusedCell()
      if (!focusedCell) return

      let nextRow = focusedCell.rowIndex
      let nextCol = focusedCell.column.colId

      // Calculate next cell position based on arrow key
      switch (key) {
        case 'ArrowUp':
          nextRow = Math.max(0, focusedCell.rowIndex - 1)
          break
        case 'ArrowDown':
          nextRow = focusedCell.rowIndex + 1
          break
        case 'ArrowLeft':
          // Find previous editable column
          const allColumns = this.gridApi.getAllDisplayedColumns()
          const currentColIndex = allColumns.findIndex(col => col.colId === focusedCell.column.colId)
          if (currentColIndex > 0) {
            nextCol = allColumns[currentColIndex - 1].colId
          }
          break
        case 'ArrowRight':
          // Find next editable column
          const allCols = this.gridApi.getAllDisplayedColumns()
          const currentColIdx = allCols.findIndex(col => col.colId === focusedCell.column.colId)
          if (currentColIdx < allCols.length - 1) {
            nextCol = allCols[currentColIdx + 1].colId
          }
          break
      }

      // Stop current editing and navigate
      this.gridApi.stopEditing()

      // Navigate to next cell
      this.gridApi.setFocusedCell(nextRow, nextCol)
    }
  }

  calculatePinnedBottomRow() {
    const filteredData = []
    const uniqueClients = new Set()

    this.gridApi.forEachNodeAfterFilter(node => {
      if (node.data && Object.keys(node.data).length > 0) {
        filteredData.push(node.data)

        let key = null

        if (this.forCaregiverValue) {
          key = node.data['client_care_relationship_id']
        } else {
          key = node.data['Client Mon Ami ID']
        }

        if (this.forEmployeeValue) {
          key = `${key}_${node.data['employee_id']}`
        }

        uniqueClients.add(key)
      }
    })

    if (filteredData.length === 0) {
      return [{}]
    }

    const totals = {}

    for (let i = 1; i <= MAX_DAYS; i++) {
      const dayField = `days_${i}`
      totals[dayField] = formatNumber(
        filteredData.reduce((sum, row) => {
          const val = parseFloat(row[dayField]) || 0
          return sum + val
        }, 0)
      )
    }

    const allColumns = this.gridApi.getAllDisplayedColumns()
    const clientOrCaregiverField = this.forCaregiverValue
      ? COLUMNS.CAREGIVER
      : this.forEmployeeValue
        ? COLUMNS.CLIENT
        : COLUMNS.FIRST_NAME
    const targetColumn = allColumns.find(col => col.colId === clientOrCaregiverField)

    let text = `Total: ${uniqueClients.size} unduplicated clients`
    if (this.forCaregiverValue) {
      text = `Total: ${uniqueClients.size} unduplicated care relationships`
    }

    if (this.forEmployeeValue) {
      text = text.replace('unduplicated', 'unduplicated employee -')
    }
    totals[targetColumn.colId] = text

    return [totals]
  }
}
